import * as Yup from 'yup';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Icon,
  Segment,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Message,
  Dimmer,
  Loader,
  Confirm,
  Dropdown,
  Grid,
  Card
} from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import Select from 'react-select';
import { countryOptions } from '../../app/common/options/countryOptions';
import { LocationPicker } from '../../components/map/LocationPicker';
import { useStore } from '../../app/stores/store';
import { Company } from '../../app/models/Company';
import { agent } from '../../app/api/agent';
import { Formik } from 'formik';

// Helper function to create localized country options
const createCountryOptions = (t: any, originalCountryOptions: any[]) => 
  originalCountryOptions.map(option => ({
    ...option,
    label: t(`countries.${option.label.toLowerCase()}`) || option.label
  }));

// Define the props interface for CompanyForm
interface CompanyFormProps {
    company: Company;
    onClose: () => void;
    onSubmit: (company: Company) => void;
  }

const CompanyForm = observer(({ company, onClose, onSubmit }: CompanyFormProps) => {
  const { t } = useTranslation();
  
  const validationSchema = Yup.object({
    companyName: Yup.string().required(t('companies.form.companyNameRequired')),
    companyTaxNumber: Yup.string().required(t('companies.form.taxNumberRequired')),
    companyIdentificationNumber: Yup.string().required(t('companies.form.identificationNumberRequired')),
    city: Yup.string().required(t('companies.form.cityRequired')),
    postalCode: Yup.string().required(t('companies.form.postalCodeRequired')),
    address: Yup.string().required(t('companies.form.addressRequired')),
    phone: Yup.string().required(t('companies.form.phoneRequired')),
    email: Yup.string().email(t('companies.form.emailInvalid')).required(t('companies.form.emailRequired')),
  });

  const localizedCountryOptions = createCountryOptions(t, countryOptions);

  return (
    <Formik
      initialValues={company}
      validationSchema={validationSchema}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors, touched, setFieldValue, isValid, isSubmitting, dirty }) => (
        <Form onSubmit={handleSubmit} error>
          <Form.Field>
            <label>{t('companies.form.companyName')}</label>
            <Input
              name="companyName"
              placeholder={t('companies.form.companyNamePlaceholder')}
              value={values.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.companyName && errors.companyName ? true : false}
            />
            {touched.companyName && errors.companyName && (
              <Message error content={errors.companyName} />
            )}
          </Form.Field>

          <Form.Field>
            <label>{t('companies.form.taxNumber')}</label>
            <Input
              name="companyTaxNumber"
              placeholder={t('companies.form.taxNumberPlaceholder')}
              value={values.companyTaxNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.companyTaxNumber && errors.companyTaxNumber ? true : false}
            />
            {touched.companyTaxNumber && errors.companyTaxNumber && (
              <Message error content={errors.companyTaxNumber} />
            )}
          </Form.Field>

          <Form.Field>
            <label>{t('companies.form.identificationNumber')}</label>
            <Input
              name="companyIdentificationNumber"
              placeholder={t('companies.form.identificationNumberPlaceholder')}
              value={values.companyIdentificationNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.companyIdentificationNumber && errors.companyIdentificationNumber ? true : false}
            />
            {touched.companyIdentificationNumber && errors.companyIdentificationNumber && (
              <Message error content={errors.companyIdentificationNumber} />
            )}
          </Form.Field>

          <Form.Group widths="equal" stackable>
            <Form.Field>
              <label>{t('companies.form.city')}</label>
              <Input
                name="city"
                placeholder={t('companies.form.cityPlaceholder')}
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.city && errors.city ? true : false}
              />
              {touched.city && errors.city && (
                <Message error content={errors.city} />
              )}
            </Form.Field>

            <Form.Field>
              <label>{t('companies.form.postalCode')}</label>
              <Input
                name="postalCode"
                placeholder={t('companies.form.postalCodePlaceholder')}
                value={values.postalCode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.postalCode && errors.postalCode ? true : false}
              />
              {touched.postalCode && errors.postalCode && (
                <Message error content={errors.postalCode} />
              )}
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label>{t('companies.form.address')}</label>
            <Input
              name="address"
              placeholder={t('companies.form.addressPlaceholder')}
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.address && errors.address ? true : false}
            />
            {touched.address && errors.address && (
              <Message error content={errors.address} />
            )}
          </Form.Field>

          <Form.Group widths="equal" stackable>
            <Form.Field>
              <label>{t('companies.form.phone')}</label>
              <Input
                name="phone"
                placeholder={t('companies.form.phonePlaceholder')}
                value={values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.phone && errors.phone ? true : false}
              />
              {touched.phone && errors.phone && (
                <Message error content={errors.phone} />
              )}
            </Form.Field>

            <Form.Field>
              <label>{t('companies.form.email')}</label>
              <Input
                name="email"
                placeholder={t('companies.form.emailPlaceholder')}
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && errors.email ? true : false}
              />
              {touched.email && errors.email && (
                <Message error content={errors.email} />
              )}
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label>{t('companies.form.country')}</label>
            <Select
              options={localizedCountryOptions}
              value={localizedCountryOptions.find(option => option.value === values.country?.id) || null}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  setFieldValue('country', {
                    id: selectedOption.value,
                    countryName: selectedOption.label
                  });
                }
              }}
              placeholder={t('companies.form.countryPlaceholder')}
              isSearchable
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 1000 })
              }}
              menuPortalTarget={document.body}
            />
          </Form.Field>

          <Header as="h4" dividing>{t('companies.form.location')}</Header>
          <LocationPicker
            onLocationSelect={(lat, lng) => {
              setFieldValue('latitude', lat);
              setFieldValue('longitude', lng);
            }}
            initialLat={values.latitude}
            initialLng={values.longitude}
          />

          <Button.Group fluid style={{ marginTop: '20px' }} stackable>
            <Button type="button" onClick={onClose}>{t('companies.form.cancel')}</Button>
            <Button.Or />
            <Button 
              positive 
              type="submit" 
              disabled={!isValid || isSubmitting || !dirty}
              loading={isSubmitting}
            >
              {t('companies.form.save')}
            </Button>
          </Button.Group>
        </Form>
      )}
    </Formik>
  );
});

export default observer(function Companies() {
  const { t } = useTranslation();
  const { companyStore, userStore } = useStore();
  const { loadUserCompanies, companyRegistry, loading, loadingInitial } = companyStore;
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isNewCompany, setIsNewCompany] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (userStore.user) {
      loadUserCompanies(userStore.user.userID);
    }
  }, [loadUserCompanies, userStore.user]);

  const handleCreateCompany = () => {
    setIsNewCompany(true);
    setSelectedCompany({
      id: '', // ID will be assigned by the server
      companyName: '',
      companyTaxNumber: '',
      companyIdentificationNumber: '',
      city: '',
      postalCode: '',
      address: '',
      phone: '',
      email: '',
      latitude: 0,
      longitude: 0
    });
    setModalOpen(true);
  };

  const handleEditCompany = (company: Company) => {
    setIsNewCompany(false);
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleDeleteClick = (company: Company) => {
    setSelectedCompany(company);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedCompany) {
      await companyStore.deleteCompany(selectedCompany.id);
      setDeleteConfirmOpen(false);
    }
  };

  const handleSubmit = async (company: Company) => {
    try {
      if (isNewCompany) {
        // Don't assign ID here, let server generate it
        await companyStore.createCompany(company);
        
        // Reload companies to ensure we have the latest data with server-generated IDs
        if (userStore.user) {
          await loadUserCompanies(userStore.user.userID);
        }
      } else {
        await companyStore.updateCompany(company);
      }
      
      setModalOpen(false);
    } catch (error) {
      console.error("Error submitting company:", error);
    }
  };

  if (loadingInitial) {
    return (
      <Segment>
        <Dimmer active inverted>
          <Loader inverted content={t('companies.loadingCompanies')} />
        </Dimmer>
      </Segment>
    );
  }

  return (
    <Container fluid>
          <style>{`
            @media only screen and (max-width: 767px) {
              .ui.container.fluid {
                margin-left: 0 !important;
                margin-right: 0 !important;
                padding-left: 0.5em !important;
                padding-right: 0.5em !important;
              }
              
              .ui.segment.clearing {
                padding: 0.5em !important;
              }
              
              .ui.table.mobile-card {
                border: none !important;
              }
              
              .ui.table.mobile-card thead {
                display: none;
              }
              
              .ui.table.mobile-card tbody {
                display: block;
              }
              
              .ui.table.mobile-card tr {
                display: block;
                margin-bottom: 1em;
                border: 1px solid rgba(34, 36, 38, 0.15);
                border-radius: 0.28571429rem;
                box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15);
              }
              
              .ui.table.mobile-card td {
                display: flex;
                flex-direction: column;
                padding: 0.5em !important;
                border: none !important;
                text-align: left !important;
              }
              
              .ui.table.mobile-card td:before {
                content: attr(data-label);
                font-weight: bold;
                margin-bottom: 0.3em;
                color: rgba(0, 0, 0, 0.87);
                font-size: 0.9em;
              }
              
              .ui.table.mobile-card td:empty {
                display: none;
              }
              
              .mobile-actions {
                display: flex;
                justify-content: space-around;
                margin-top: 0.5em;
              }
              
              .ui.header h2 {
                font-size: 1.3em !important;
              }
              
              .ui.modal {
                width: 95% !important;
                margin: 1em auto !important;
              }
              
              .ui.modal > .content {
                padding: 1em !important;
              }
              
              /* Working Hours Table Mobile Styles */
              .ui.table.celled.compact thead th {
                font-size: 0.85em !important;
                padding: 0.4em 0.3em !important;
              }
              
              .ui.table.celled.compact thead th button {
                display: block !important;
                margin: 0.2em auto 0 auto !important;
                padding: 0.3em 0.4em !important;
              }
              
              .ui.table.celled.compact tbody td {
                padding: 0.4em 0.3em !important;
              }
              
              .ui.table.celled.compact tbody td .ui.input {
                font-size: 0.9em !important;
              }
              
              .ui.table.celled.compact tbody td .ui.input input {
                padding: 0.5em !important;
              }
            }
          `}</style>
      <Header as="h2">
        <Icon name="building" />
        <Header.Content>{t('companies.title')}</Header.Content>
      </Header>

      <Segment clearing>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={16}>
              {Array.from(companyRegistry.values()).length === 0 && (
                <Message info>
                  <Message.Header>{t('companies.noCompaniesFound')}</Message.Header>
                  <p>{t('companies.noCompaniesDescription')}</p>
                </Message>
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16} textAlign="right">
              <Button 
                primary 
                icon 
                labelPosition="left" 
                onClick={handleCreateCompany}
                fluid={isMobile}
              >
                <Icon name="add" />
                {t('companies.addCompany')}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        
        {Array.from(companyRegistry.values()).length > 0 && (
          <>
            {/* Desktop Table View */}
            {!isMobile && (
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>{t('companies.table.companyName')}</Table.HeaderCell>
                    <Table.HeaderCell>{t('companies.table.taxNumber')}</Table.HeaderCell>
                    <Table.HeaderCell>{t('companies.table.city')}</Table.HeaderCell>
                    <Table.HeaderCell>{t('companies.table.address')}</Table.HeaderCell>
                    <Table.HeaderCell>{t('companies.table.contact')}</Table.HeaderCell>
                    <Table.HeaderCell>{t('companies.table.country')}</Table.HeaderCell>
                    <Table.HeaderCell width={2}>{t('companies.table.actions')}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                
                <Table.Body>
                  {Array.from(companyRegistry.values()).map((company) => (
                    <Table.Row key={company.id}>
                      <Table.Cell>{company.companyName}</Table.Cell>
                      <Table.Cell>{company.companyTaxNumber}</Table.Cell>
                      <Table.Cell>{company.city}</Table.Cell>
                      <Table.Cell>{company.address}</Table.Cell>
                      <Table.Cell>
                        <Icon name="phone" /> {company.phone}<br />
                        <Icon name="mail" /> {company.email}
                      </Table.Cell>
                      <Table.Cell>{company.country?.countryName || t('companies.table.countryNotSpecified')}</Table.Cell>
                      <Table.Cell>
                        <Button.Group size="mini">
                          <Button 
                            icon="edit" 
                            color="blue" 
                            onClick={() => handleEditCompany(company)} 
                          />
                          <Button 
                            icon="trash" 
                            color="red" 
                            onClick={() => handleDeleteClick(company)} 
                          />
                        </Button.Group>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

            {/* Mobile Card View */}
            {isMobile && (
              <Card.Group stackable>
                {Array.from(companyRegistry.values()).map((company) => (
                  <Card key={company.id} fluid>
                    <Card.Content>
                      <Card.Header>{company.companyName}</Card.Header>
                      <Card.Meta>
                        {t('companies.table.taxNumber')}: {company.companyTaxNumber}
                      </Card.Meta>
                      <Card.Description>
                        <p>
                          <Icon name="map marker alternate" />
                          {company.city}, {company.address}
                        </p>
                        <p>
                          <Icon name="phone" /> {company.phone}
                        </p>
                        <p>
                          <Icon name="mail" /> {company.email}
                        </p>
                        {company.country && (
                          <p>
                            <Icon name="globe" /> {company.country.countryName}
                          </p>
                        )}
                      </Card.Description>
                    </Card.Content>
                    <Card.Content extra>
                      <Button.Group fluid>
                        <Button 
                          icon="edit" 
                          color="blue" 
                          onClick={() => handleEditCompany(company)}
                        />
                        <Button 
                          icon="trash" 
                          color="red" 
                          onClick={() => handleDeleteClick(company)}
                        />
                      </Button.Group>
                    </Card.Content>
                  </Card>
                ))}
              </Card.Group>
            )}
          </>
        )}
      </Segment>

      {/* Create/Edit Company Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="large"
      >
        <Modal.Header>
          {isNewCompany ? t('companies.createNewCompany') : t('companies.editCompany')}
        </Modal.Header>
        <Modal.Content scrolling>
          {selectedCompany && (
            <CompanyForm 
              company={selectedCompany} 
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          )}
        </Modal.Content>
      </Modal>

      {/* Delete Confirmation */}
      <Confirm
        open={deleteConfirmOpen}
        header={t('companies.deleteCompanyTitle', { companyName: selectedCompany?.companyName })}
        content={t('companies.deleteCompanyContent')}
        confirmButton={t('companies.deleteButton')}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Container>
  );
});